import { isBlankMap } from "__common/constants/map";
import { state } from "__common/store";
import { createDimensionBG } from "__editor/googleMapsRoofsSelector/models/advanceRoofSelecting";

// Returns pixel coords of each array's top-left panel.
//
// Lite-only stable-ordering branch: `Object.values()` keyed by groupId
// sorts numeric string keys ascending. `splitArraysIntoSubarrays`
// (panelsEpics → updatePanelGroupIdsToReflectActualGroups) re-issues every
// groupId via `nextUid()` on each ADD_PANELS, and processes landscape and
// portrait panels separately, so a newly-added panel in the minority
// orientation gets the SMALLEST fresh groupId — pushing it to the front of
// the numeric-sorted output and shifting all older arrays up by one.
//
// Panel `id` is also from `nextUid()` but is assigned at creation time and
// never reissued, so the minimum panel id within a group is a stable age
// signal. Sort groups by that and the numbering stays consistent across
// orientation flips and array additions.
//
// Legacy UBuilder keeps the original Object.values() path so existing
// projects render numbers identically to how they did before.
const getPixelCoordsForTopLeftPanelOfArray = (): Array<{ x: number; y: number }> => {
  const { panels: { panels }, app } = state();

  if (app && app.isUbuilderLite) {
    const acc = new Map<number, { x: number; y: number; firstId: number }>();
    panels.forEach(({ id, groupId, x, y }) => {
      x = Math.round(x);
      y = Math.round(y);
      const existing = acc.get(groupId);
      if (!existing) {
        acc.set(groupId, { x, y, firstId: id });
        return;
      }
      if (id < existing.firstId) existing.firstId = id;
      if (y < existing.y) {
        existing.y = y;
        existing.x = x;
      } else if (y === existing.y && x < existing.x) {
        existing.x = x;
      }
    });
    return Array.from(acc.values())
      .sort((a, b) => a.firstId - b.firstId)
      .map(({ x, y }) => ({ x, y }));
  }

  return Object.values(
    panels.reduce(
      (acc, {groupId, x, y}) => {
        // y(decimal value) is not same for all panels in a same row & Similarly for column.
        // Hence ignoring the precision(decimal value) assuming it doesn't impact.
        x = Math.round(x), y = Math.round(y);
        acc[groupId] = acc[groupId] ?? { x, y };
        if(y < acc[groupId].y){
          acc[groupId].y = y;
          acc[groupId].x = x;
        }
        acc[groupId].x = y===acc[groupId].y ? Math.min(acc[groupId].x, x) : acc[groupId].x; // same row
        return acc;
      },
    {})
  );
};

export default function arrayNumberingModel(scale: number): PIXI.Graphics {
    const { roofsSelector: { mapType }, settings: { canvasCenter } } = state();
    const roofEdgeDimensions = new PIXI.Graphics();
    const coordsInPx = getPixelCoordsForTopLeftPanelOfArray();
    const baseFontSize = 14;

    const fontStyle = new PIXI.TextStyle({
      fontSize: baseFontSize,
      fontWeight: "500",
      fill: 0xffffff,
    });

    const isBlank = isBlankMap(mapType);
    const backgroundTint = isBlank ? 0x000000 : 0x0B5394;

    coordsInPx.forEach((coord, index) => {
      const numberText = new PIXI.Text(`${index + 1}`, fontStyle);
      numberText.anchor.set(0.5);
      numberText.scale.set(1 / scale);
      numberText.x = coord.x + canvasCenter.x;
      numberText.y = coord.y + canvasCenter.y - 5 / scale - scale / 2;
      numberText.zIndex = 99999;
      const numberBg = createDimensionBG(numberText);
      numberBg.width *= 1.5;
      numberBg.tint = backgroundTint;

      roofEdgeDimensions.addChild(numberBg, numberText);
    });
    roofEdgeDimensions.zIndex = 99999;
    return roofEdgeDimensions;
    }